/**
 * Classe customizada para erro de email não validado
 */
export class EmailNotValidatedError extends Error {
    public readonly instructions?: string[];
    public readonly supportMessage?: string;
    public readonly canResendEmail?: boolean;
    public readonly resendEndpoint?: string;

    constructor(
        message: string,
        options?: {
            instructions?: string[];
            supportMessage?: string;
            canResendEmail?: boolean;
            resendEndpoint?: string;
        }
    ) {
        super(message);
        this.name = 'EmailNotValidatedError';
        this.instructions = options?.instructions;
        this.supportMessage = options?.supportMessage;
        this.canResendEmail = options?.canResendEmail;
        this.resendEndpoint = options?.resendEndpoint;
        
        // Mantém o stack trace correto
        Object.setPrototypeOf(this, EmailNotValidatedError.prototype);
    }
}

/**
 * Classe customizada para erro de autenticação
 */
export class AuthenticationError extends Error {
    constructor(message: string = 'Usuário e/ou senha inválido(s)') {
        super(message);
        this.name = 'AuthenticationError';
        Object.setPrototypeOf(this, AuthenticationError.prototype);
    }
}

/**
 * Classe customizada para erro de sessão expirada
 * Usada para distinguir erros de sessão expirada (comportamento esperado) de outros erros
 * Este erro não deve ser reportado ao Sentry pois é um comportamento esperado do sistema
 */
export class SessionExpiredError extends Error {
    public readonly isSessionExpired: boolean = true;
    public readonly reason?: string;

    constructor(
        message: string = 'Sessão expirada. Faça login novamente.',
        reason?: string
    ) {
        super(message);
        this.name = 'SessionExpiredError';
        this.isSessionExpired = true;
        this.reason = reason;
        Object.setPrototypeOf(this, SessionExpiredError.prototype);
    }
}

/**
 * Classe customizada para erro de rede/conexão
 * Usada para distinguir erros de rede temporários de erros de autenticação
 */
export class NetworkError extends Error {
    public readonly isNetworkError: boolean = true;
    public readonly originalError?: unknown;
    public readonly httpStatus?: number;
    public readonly code?: string;

    constructor(
        message: string = 'Erro de conexão. Verifique sua internet e tente novamente.',
        options?: {
            originalError?: unknown;
            httpStatus?: number;
            code?: string;
        }
    ) {
        super(message);
        this.name = 'NetworkError';
        this.originalError = options?.originalError;
        this.httpStatus = options?.httpStatus;
        this.code = options?.code;
        Object.setPrototypeOf(this, NetworkError.prototype);
    }
}

/**
 * Verifica se um erro é um NetworkError ou um erro de rede do Axios
 * 
 * Esta função identifica erros que indicam problemas de conectividade ou infraestrutura,
 * distinguindo-os de erros de autenticação ou validação. Isso é importante para não
 * limpar tokens de sessão em caso de problemas temporários de rede.
 * 
 * @param error - Erro a ser verificado
 * @returns true se for erro de rede, false caso contrário
 * 
 * @example
 * ```typescript
 * try {
 *   await refreshAccessToken();
 * } catch (error) {
 *   if (isNetworkError(error)) {
 *     // Não limpar tokens - problema temporário de rede
 *     console.log('Erro de rede temporário');
 *   } else {
 *     // Limpar tokens - erro de autenticação
 *     clearAuthData();
 *   }
 * }
 * ```
 */
export function isNetworkError(error: unknown): error is NetworkError | (Error & { request?: unknown; response?: never }) {
    // Verifica se é uma instância de NetworkError
    if (error instanceof NetworkError) {
        return true;
    }
    
    // Verifica se tem a propriedade isNetworkError (para compatibilidade com erros customizados)
    if (error && typeof error === 'object' && 'isNetworkError' in error) {
        return (error as any).isNetworkError === true;
    }
    
    // Verifica se é um erro de timeout ou conexão (códigos de erro do Node.js/Axios)
    // Esta verificação deve vir ANTES da verificação de response para garantir que
    // erros de timeout sejam detectados mesmo se tiverem response
    // ECONNABORTED: Timeout do Axios
    // ETIMEDOUT: Timeout de conexão
    // ENOTFOUND: DNS não encontrado
    // ECONNREFUSED: Conexão recusada
    if (error && typeof error === 'object' && 'code' in error) {
        const code = (error as any).code;
        if (code === 'ECONNABORTED' || code === 'ETIMEDOUT' || code === 'ENOTFOUND' || code === 'ECONNREFUSED') {
            return true;
        }
    }
    
    // Verifica se é um erro HTTP 5xx (erro de infraestrutura/servidor)
    // Tratamos 5xx como erro de rede porque indica problema no servidor/infraestrutura,
    // não um problema de autenticação. Para refresh de token, isso é especialmente importante
    // pois não devemos limpar tokens válidos se o servidor estiver temporariamente indisponível.
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        const status = axiosError.response?.status;
        if (status && status >= 500 && status < 600) {
            return true;
        }
    }
    
    // Verifica se é um erro do Axios sem resposta (erro de rede puro)
    // Isso acontece quando a requisição foi feita mas não houve resposta do servidor
    // ERR_CANCELED é excluído: indica AbortController intencional, não falha de rede
    if (error && typeof error === 'object' && 'request' in error && !('response' in error)) {
        const code = (error as any).code;
        if (code === 'ERR_CANCELED') return false;
        return true;
    }
    
    return false;
}

/**
 * Verifica se um erro é um SessionExpiredError
 * 
 * @param error - Erro a ser verificado
 * @returns true se for erro de sessão expirada, false caso contrário
 */
export function isSessionExpiredError(error: unknown): error is SessionExpiredError {
    if (error instanceof SessionExpiredError) {
        return true;
    }
    
    if (error && typeof error === 'object' && 'isSessionExpired' in error) {
        const isSessionExpired = (error as any).isSessionExpired;
        return typeof isSessionExpired === 'boolean' && isSessionExpired === true;
    }
    
    return false;
}

